import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, MoreHorizontal, Pencil, Plus, RotateCcw, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, Money, PageHeader, TableSkeleton } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";
import AddressFields, { EMPTY_ADDRESS, addressFromParty, addressText } from "../components/AddressFields";

const EMPTY = { name: "", type: "customer", legal_name: "", contact_person: "", mobile: "", email: "",
  gstin: "", pan: "", billing_address: "", shipping_address: "", city: "", state: "", pin: "",
  credit_limit: 0, credit_days: 0, opening_balance: 0, notes: "",
  billing_address_details: EMPTY_ADDRESS, shipping_address_details: EMPTY_ADDRESS };
const TYPES = ["customer", "client", "supplier", "vendor", "contractor", "dealer", "other"];

function PartyDialog({ open, onClose, party, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (party) {
      setForm({ ...EMPTY, ...party });
      setBillingAddress(addressFromParty(party, "billing"));
      const sh = addressFromParty(party, "shipping");
      setShippingAddress(sh.line1 ? sh : addressFromParty(party, "billing"));
    } else {
      setForm(EMPTY); setBillingAddress(EMPTY_ADDRESS); setShippingAddress(EMPTY_ADDRESS);
    }
  }, [party, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Party name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, billing_address_details: billingAddress, billing_address: addressText(billingAddress), shipping_address_details: shippingAddress, shipping_address: addressText(shippingAddress), credit_limit: +form.credit_limit || 0, credit_days: +form.credit_days || 0,
        opening_balance: +form.opening_balance || 0 };
      if (party) await api.put(`/parties/${party.id}`, payload);
      else await api.post("/parties", payload);
      toast.success(party ? "Party updated" : "Party created");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="party-dialog">
        <DialogHeader><DialogTitle className="font-display">{party ? "Edit Party" : "New Party"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Party Name *</Label><Input data-testid="party-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="party-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Contact Person</Label><Input className="mt-1.5" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
          <div><Label>Mobile</Label><Input data-testid="party-mobile" className="mt-1.5" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
          <div><Label>Email</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><Label>GSTIN</Label><Input data-testid="party-gstin" className="mt-1.5 font-mono" placeholder="19AAAAA0000A1Z5" value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} /></div>
          <div><Label>PAN</Label><Input className="mt-1.5 font-mono" value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} /></div>
          <div><Label>State</Label><Input data-testid="party-state" className="mt-1.5" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
          <div><Label>City</Label><Input className="mt-1.5" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
          <div><Label>PIN</Label><Input className="mt-1.5" value={form.pin} onChange={(e) => set("pin", e.target.value)} /></div>
          <div className="sm:col-span-2">
            <AddressFields title="Billing Address" value={billingAddress} onChange={(v) => { setBillingAddress(v); set("billing_address", addressText(v)); }} icon="billing" copyLabel="Copy to Shipping" onCopy={() => setShippingAddress({ ...billingAddress })} />
          </div>
          <div className="sm:col-span-2">
            <AddressFields title="Shipping Address" value={shippingAddress} onChange={(v) => { setShippingAddress(v); set("shipping_address", addressText(v)); }} icon="shipping" copyLabel="Copy Billing Address" onCopy={() => setShippingAddress({ ...billingAddress })} />
          </div>
          <div><Label>Credit Limit (₹)</Label><Input type="number" className="mt-1.5" value={form.credit_limit} onChange={(e) => set("credit_limit", e.target.value)} /></div>
          <div><Label>Credit Days</Label><Input type="number" className="mt-1.5" value={form.credit_days} onChange={(e) => set("credit_days", e.target.value)} /></div>
          {!party && <div><Label>Opening Balance (₹)</Label><Input type="number" className="mt-1.5" value={form.opening_balance} onChange={(e) => set("opening_balance", e.target.value)} /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="party-save-btn" onClick={save} disabled={busy}
            className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Save Party"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Parties() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [archived, setArchived] = useState(false);
  const [dialog, setDialog] = useState({ open: false, party: null });
  const [params] = useSearchParams();
  const nav = useNavigate();

  const load = () => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (type !== "all") qs.set("type", type);
    qs.set("archived", archived);
    api.get(`/parties?${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [activeId, type, archived]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line
  useEffect(() => { if (params.get("new") === "1") setDialog({ open: true, party: null }); }, [params]);

  const archive = async (p, restore = false) => {
    try {
      await api.post(`/parties/${p.id}/${restore ? "restore" : "archive"}`);
      toast.success(restore ? "Party restored" : "Party archived");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="parties-page">
      <PageHeader title="Parties" testId="parties-title" sub="Customers, suppliers, vendors, contractors & dealers">
        <Button data-testid="new-party-btn" onClick={() => setDialog({ open: true, party: null })}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Party</Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="parties-search" className="pl-9 bg-white dark:bg-slate-900" placeholder="Search name, GSTIN, mobile..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="parties-type-filter" className="w-40 bg-white dark:bg-slate-900"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={archived ? "default" : "outline"} data-testid="parties-archived-toggle"
          onClick={() => setArchived(!archived)}>
          <Archive className="mr-1.5 h-4 w-4" /> {archived ? "Viewing archived" : "Archived"}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="parties-empty" title={archived ? "No archived parties" : "No parties yet"}
            body="Add your first customer or supplier to start billing."
            actionLabel="New Party" actionTo="/parties?new=1" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="parties-table">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3">Type</th>
                  <th className="p-3">Contact</th><th className="p-3">GSTIN</th>
                  <th className="p-3 text-right">Outstanding</th><th className="p-3 text-right">Advance</th><th className="p-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.id} data-testid={`party-row-${p.code}`} onClick={() => nav(`/parties/${p.id}`)}
                    className="cursor-pointer border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="p-3 font-mono text-xs text-slate-500">{p.code}</td>
                    <td className="p-3 font-medium">
                      {p.name} {p.is_demo && <Badge variant="secondary" className="ml-1.5 text-[10px]">DEMO</Badge>}
                    </td>
                    <td className="p-3 capitalize text-slate-500">{p.type}</td>
                    <td className="p-3 text-slate-500">{p.mobile || "—"}</td>
                    <td className="p-3 font-mono text-xs text-slate-500">{p.gstin || "—"}</td>
                    <td className="p-3 text-right"><Money value={p.outstanding} className={p.outstanding > 0 ? "text-red-600 dark:text-red-400 font-semibold" : "text-slate-400"} /></td>
                    <td className="p-3 text-right"><Money value={p.advance} className={p.advance > 0 ? "text-emerald-600 dark:text-emerald-400 font-semibold" : "text-slate-400"} /></td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`party-actions-${p.code}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white dark:bg-slate-900">
                          <DropdownMenuItem onClick={() => setDialog({ open: true, party: p })} className="cursor-pointer"><Pencil className="mr-2 h-3.5 w-3.5" /> Edit</DropdownMenuItem>
                          {archived
                            ? <DropdownMenuItem onClick={() => archive(p, true)} className="cursor-pointer"><RotateCcw className="mr-2 h-3.5 w-3.5" /> Restore</DropdownMenuItem>
                            : <DropdownMenuItem onClick={() => archive(p)} className="cursor-pointer"><Archive className="mr-2 h-3.5 w-3.5" /> Archive</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">{data?.total ?? 0} parties</p>
      <PartyDialog open={dialog.open} party={dialog.party} onClose={() => setDialog({ open: false, party: null })} onSaved={load} />
    </div>
  );
}
